import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DatePickerField } from '../components/DatePickerField';
import { Package, Plus, AlertTriangle, TrendingDown, Boxes } from 'lucide-react';
import { format } from 'date-fns';

const fmt = (n) => new Intl.NumberFormat('en-KE', { maximumFractionDigits: 2 }).format(Number(n || 0));
const fmtKES = (n) => new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0 }).format(n || 0);

export default function CommodityInventory() {
  const [factory, setFactory] = useState('All');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [adjDialogOpen, setAdjDialogOpen] = useState(false);
  const [itemForm, setItemForm] = useState({ name: '', unit: 'pieces', reorder_level: 0 });
  const [adjForm, setAdjForm] = useState({ commodity_id: '', factory: '', date: new Date(), adjustment_type: 'opening', quantity: '', reason: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/commodity-inventory/summary', { params: { factory } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [factory]);

  useEffect(() => { load(); }, [load]);

  const saveItem = async (e) => {
    e.preventDefault();
    try {
      await api.post('/commodity-items', { name: itemForm.name, unit: itemForm.unit, reorder_level: parseFloat(itemForm.reorder_level) || 0 });
      toast.success('Commodity saved');
      setItemDialogOpen(false); setItemForm({ name: '', unit: 'pieces', reorder_level: 0 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const saveAdj = async (e) => {
    e.preventDefault();
    try {
      await api.post('/commodity-adjustments', {
        commodity_id: adjForm.commodity_id,
        factory: adjForm.factory || null,
        date: format(adjForm.date, 'yyyy-MM-dd'),
        adjustment_type: adjForm.adjustment_type,
        quantity: parseFloat(adjForm.quantity),
        reason: adjForm.reason || null,
      });
      toast.success('Adjustment recorded');
      setAdjDialogOpen(false); setAdjForm({ commodity_id: '', factory: '', date: new Date(), adjustment_type: 'opening', quantity: '', reason: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  if (loading || !data) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]" /></div>;

  const items = data.items || [];
  const lowStockCount = items.filter(i => i.low_stock).length;

  return (
    <div className="space-y-6" data-testid="commodity-inventory-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]">Commodity / Packaging Stock</h1>
          <p className="text-slate-500 mt-1 text-sm">Estimated operational balances. Auto-consumed by Production based on configured rates.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Factory:</Label>
          <Select value={factory} onValueChange={setFactory}>
            <SelectTrigger className="w-40 text-sm" data-testid="commodity-factory-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Factories</SelectItem>
              <SelectItem value="Ndumberi">Ndumberi</SelectItem>
              <SelectItem value="Githunguri">Githunguri</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
            <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="add-commodity-item-btn" className="gap-1"><Plus className="h-3.5 w-3.5" />Commodity</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Commodity Item</DialogTitle></DialogHeader>
              <form onSubmit={saveItem} className="space-y-3">
                <div className="space-y-1"><Label>Name</Label><Input value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} placeholder="e.g., Bailers" required data-testid="commodity-name-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Unit</Label><Input value={itemForm.unit} onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })} placeholder="pieces / rolls / kg" data-testid="commodity-unit-input" /></div>
                  <div className="space-y-1"><Label>Reorder Level</Label><Input type="number" value={itemForm.reorder_level} onChange={(e) => setItemForm({ ...itemForm, reorder_level: e.target.value })} /></div>
                </div>
                <DialogFooter><Button type="submit" className="bg-[#1B4D3E]" data-testid="commodity-save-btn">Save</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={adjDialogOpen} onOpenChange={setAdjDialogOpen}>
            <DialogTrigger asChild><Button size="sm" className="bg-[#1B4D3E] gap-1" data-testid="add-adjustment-btn"><Plus className="h-3.5 w-3.5" />Adjustment</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Stock Adjustment / Opening Balance</DialogTitle></DialogHeader>
              <form onSubmit={saveAdj} className="space-y-3">
                <div className="space-y-1"><Label>Commodity</Label>
                  <Select value={adjForm.commodity_id} onValueChange={(v) => setAdjForm({ ...adjForm, commodity_id: v })}>
                    <SelectTrigger data-testid="adj-commodity"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{items.map(i => <SelectItem key={i.id} value={i.id}>{i.name} ({i.unit})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Date</Label><DatePickerField date={adjForm.date} onDateChange={(d) => setAdjForm({ ...adjForm, date: d })} /></div>
                  <div className="space-y-1"><Label>Factory</Label>
                    <Select value={adjForm.factory} onValueChange={(v) => setAdjForm({ ...adjForm, factory: v })}>
                      <SelectTrigger><SelectValue placeholder="(none)" /></SelectTrigger>
                      <SelectContent><SelectItem value="Ndumberi">Ndumberi</SelectItem><SelectItem value="Githunguri">Githunguri</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Type</Label>
                    <Select value={adjForm.adjustment_type} onValueChange={(v) => setAdjForm({ ...adjForm, adjustment_type: v })}>
                      <SelectTrigger data-testid="adj-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="opening">Opening Balance</SelectItem>
                        <SelectItem value="increase">Stock Increase</SelectItem>
                        <SelectItem value="reduction">Stock Reduction</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label>Quantity</Label><Input type="number" step="0.01" value={adjForm.quantity} onChange={(e) => setAdjForm({ ...adjForm, quantity: e.target.value })} required data-testid="adj-qty" /></div>
                </div>
                <div className="space-y-1"><Label>Reason / Remarks</Label><Input value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} /></div>
                <DialogFooter><Button type="submit" className="bg-[#1B4D3E]" data-testid="adj-save-btn">Save</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Commodity Items" value={items.length} icon={<Boxes className="h-4 w-4" />} />
        <SummaryCard label="Low Stock Items" value={lowStockCount} icon={<AlertTriangle className="h-4 w-4 text-amber-500" />} tone={lowStockCount ? 'text-amber-700 font-semibold' : ''} />
        <SummaryCard label="Total Consumed" value={fmt(items.reduce((s, i) => s + i.total_consumed, 0))} icon={<TrendingDown className="h-4 w-4" />} />
        <SummaryCard label="Total Purchased" value={fmt(items.reduce((s, i) => s + i.total_purchased, 0))} icon={<Package className="h-4 w-4" />} />
      </div>

      {data.overall_production_capacity_by_pack && Object.keys(data.overall_production_capacity_by_pack).length > 0 && (
        <Card><CardHeader className="pb-2"><CardTitle className="text-base">Overall Production Capacity (bales) — Bottleneck Analysis</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(data.overall_production_capacity_by_pack).map(([ps, o]) => (
                <div key={ps} className="p-3 bg-slate-50 rounded" data-testid={`capacity-${ps}`}>
                  <div className="text-xs text-slate-500">{ps} bales</div>
                  <div className="text-lg font-semibold">{fmt(o.bales)}</div>
                  <div className="text-[11px] text-amber-700">Bottleneck: {o.bottleneck}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Estimated Commodity Balances ({data.factory})</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Commodity</TableHead><TableHead>Unit</TableHead>
              <TableHead className="text-right">Opening/Adj</TableHead>
              <TableHead className="text-right">Purchased</TableHead>
              <TableHead className="text-right">Consumed</TableHead>
              <TableHead className="text-right">Est. Balance</TableHead>
              <TableHead className="text-right">Avg Cost/Unit</TableHead>
              <TableHead>Capacity (bales)</TableHead>
              <TableHead className="text-center">Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500 italic">No commodities yet — click "+ Commodity" to add.</TableCell></TableRow>
              ) : items.map(i => (
                <TableRow key={i.id} data-testid={`commodity-row-${i.id}`}>
                  <TableCell><Link to={`/commodity/${i.id}/statement`} className="text-emerald-700 font-medium hover:underline">{i.name}</Link></TableCell>
                  <TableCell>{i.unit}</TableCell>
                  <TableCell className="text-right">{fmt(i.opening_and_adjustments)}</TableCell>
                  <TableCell className="text-right">{fmt(i.total_purchased)}</TableCell>
                  <TableCell className="text-right">{fmt(i.total_consumed)}</TableCell>
                  <TableCell className={`text-right font-medium ${i.estimated_balance < 0 ? 'text-red-700' : ''}`}>{fmt(i.estimated_balance)}</TableCell>
                  <TableCell className="text-right text-xs">{i.average_purchase_cost_per_unit > 0 ? fmtKES(i.average_purchase_cost_per_unit) : '-'}</TableCell>
                  <TableCell className="text-xs">
                    {Object.entries(i.equivalent_bales_by_pack || {}).map(([ps, b]) => (
                      <span key={ps} className="inline-block mr-2 text-slate-600">{ps}: <b>{fmt(b)}</b></span>
                    ))}
                    {Object.keys(i.equivalent_bales_by_pack || {}).length === 0 && <span className="text-slate-400 italic">no rule</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {i.estimated_balance < 0 ? <Badge className="bg-red-100 text-red-700">Negative</Badge> : i.low_stock ? <Badge className="bg-amber-100 text-amber-700">Low Stock</Badge> : <Badge variant="outline">OK</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryCard({ label, value, icon, tone = '' }) {
  return (<Card><CardContent className="p-3"><div className="flex items-center justify-between"><span className="text-[11px] uppercase tracking-wider text-slate-500">{label}</span>{icon}</div><div className={`text-lg mt-1 ${tone}`}>{value}</div></CardContent></Card>);
}
